/**
 * BrandedLoader.tsx — the PATIENT-JOURNEY loading moment (UX Wave 6 §1;
 * redesigned 2026-09-24, CTO).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 6 (loaders),
 * journey look-and-feel (loaders).
 *
 * WHY: staff surfaces get page-shaped skeletons (ui/skeletons.tsx), but the
 * patient journey's load moments (journey entry, the `#resume=` / widget
 * hand-offs, the checkout hand-over, the post-payment account step) sit between
 * marketing and care — a bare spinner reads as broken there.
 *
 * 2026-09-24 (CTO): the pulsing logo tile over one fixed line ("Connecting you
 * to GP Service — Clinics") became a calmer `LoaderRing` over a line that SAYS
 * WHAT IS HAPPENING. Each mount passes its own `message` (JourneyPage picks one
 * from its flags via `journey/journeyLoaders.ts`; CheckoutFlow passes one per
 * site). The site header already carries the logo on every journey surface, so
 * the tile's brand job was being done twice.
 *
 * STILL WORKING. After {@link STILL_WORKING_AFTER_MS} a second, honest line
 * appears — "Still working — this can take a few seconds." — because a wait
 * with no change at all reads as a hang. It claims only that we are still
 * waiting, which is true for as long as this is mounted over real work. The one
 * mount that waits on NOTHING (CheckoutFlow's defensive never-should-happen
 * fallback) passes `reassure={false}`, so the line never says "working" over
 * nothing.
 *
 * ACCESSIBILITY: one `role="status"` region whose VISIBLE copy is the
 * announcement (no sr-only duplicate); the ring is decorative (`aria-hidden`).
 * A changed `message`, or the still-working line arriving, changes the region's
 * text, which a status region reads out politely.
 *
 * ⚠️ THE STATUS REGION HAS NO BUSY ANCESTOR — `aria-busy` IS ON THE RING'S BOX,
 * A SIBLING, NEVER AN ANCESTOR (review findings, 2026-09-24, two rounds).
 * The loader is never "un-busied" — it simply unmounts when the wait ends — so
 * wherever `aria-busy="true"` sits, it stays true for the loader's whole life,
 * and a live region inside a busy subtree may be held silent until the busy
 * flag clears, i.e. forever:
 *  - round 1: it sat ON the `role="status"` element. Chromium fires no
 *    live-region event at all for a busy live root (Chrome, Edge, Android
 *    TalkBack), so the message swaps and the still-working line were never
 *    announced.
 *  - round 2: it moved to a wrapper AROUND the status region. That fixed
 *    Chromium (which reads only the live root's own flag) and not Firefox:
 *    Gecko exposes `container-busy` from the nearest ancestor that sets
 *    `aria-busy`, walking PAST the live root up to the document, and NVDA drops
 *    a live-region change whose `container-busy` is "true". Same silence,
 *    another engine. (Read from the Gecko and NVDA sources, not from a live
 *    screen-reader run.)
 * So the busy element is the ring's own box — the one part that is visibly
 * "loading", and decorative — and the words are its SIBLING. Nothing that holds
 * the words is busy, at any depth. This is the structure `BuildingInterstitial`
 * uses (`aria-busy` on its checklist, the `role="status"` line beside it, not
 * inside it). Both specs pin "no busy ancestor", never "busy wrapper".
 *
 * Reduced-motion cohort gets a STILL ring — gated on the shared
 * `usePrefersReducedMotion` signal AND neutralised by the global animation kill
 * in tokens.css.
 *
 * ⚠️ PUBLIC-SAFE BY CONSTRUCTION. This one component is shared with
 * pre-eligibility screens (journey entry, an unchecked plan link), so every
 * message a caller passes must be category-level (R28) and say nothing about the
 * visitor's body or treatment — no POM name, no price, no promise of a plan.
 *
 * ── THE JOURNEY LOOK (`look="journey"`, 2026-09-25, CTO: "at the initial page
 * load, a logo-based loader … I hope you have done something interesting to
 * that as well") ──────────────────────────────────────────────────────────
 * Opt-in, so every other mount renders exactly as above. The journey entry
 * (JourneyPage) and CheckoutFlow's two PAGE-level loaders pass it; the two
 * INLINE ones (inside the account step's card and status row) keep the compact
 * ring, because a brand seal inside a card crowds the step it sits in.
 *  - THE MARK: the GP Service wordmark in the journey's deep green on a white
 *    seal, on the ivory ground, inside a hairline ring (`LoaderRing
 *    variant="halo"`) that turns slowly while its arc breathes, over a soft
 *    peach glow that breathes with it. Every moving part is a LEAF (the ring's
 *    own `<svg>`, an empty glow span) — nothing that wraps content transforms.
 *  - THE WORDS: the message in the journey's display type (light Fraunces).
 *  - THE CARD (`card`, 2026-09-25 — CTO: "use that loading page as a bit of a
 *    small content page … enough material to read till it loads", because a
 *    stage entry takes 4–5s; that evening: "I don't want to have that
 *    carousel … It should nicely load one after the other, showing the journey
 *    till the main page loads"; and ~18:00: "make the card grow as the items
 *    appear … too much text … use more icons … catchy"). On a fresh start the
 *    caller passes a {@link LoaderJourney} (`journey/entryLoaderContent.ts`,
 *    chosen by `journeyLoaderCard`) and `LoaderJourneyCard` shows it under the
 *    words: four icon-led steps of the journey and three icon chips, REVEALED
 *    ONE AFTER ANOTHER (~0.35s apart, each growing in with a fade and a small
 *    rise) and then STATIC — nothing is replaced or removed, the card simply
 *    grows as it builds up, all of it on screen by ~2.9s from mount. It is the
 *    LAST block of the loader, so what moves as it grows is the page below
 *    (the footer), never the ring, the words or an item already shown. It
 *    replaced a numbered list, a carousel (rotating panels, dots, a Pause /
 *    Play toggle) and, before that, a one-line reassurance note. It sits
 *    OUTSIDE the status region (the region still says only what is happening;
 *    nothing in the card is announced), it is drawn with the mark (never
 *    inside the show delay), it has no control (a one-time build-up that
 *    settles inside five seconds needs no pause — WCAG 2.2.2; see
 *    `LoaderJourneyCard.tsx`), and it never holds the loader up.
 *  - A SHOW DELAY, NOT A DWELL (`LOADER_SHOW_DELAY_MS`). Nothing is drawn for
 *    the first 300ms, so a quick read never flashes a loader. This component
 *    never holds itself up: it goes the moment its caller unmounts it, so
 *    CheckoutFlow's mounts can never delay a payment step (its one hold is
 *    the caller's, on the pre-plan lane only — below). The status
 *    region is in the DOM from mount, EMPTY, and the words arrive with the
 *    mark — a status region that appears with its text already in it is not
 *    reliably announced; one that gains its text is. The still-working clock
 *    still counts from MOUNT.
 *  - …AND, ONCE DRAWN, A SHORT MINIMUM — FOR THE JOURNEY ENTRY ONLY
 *    ({@link useLoaderHold}, review 2026-09-25). The show delay alone moved the
 *    flicker rather than removing it: a read landing at ~350–700ms showed the
 *    seal part-way through its 420ms fade-in and then cut to Step 0. So the
 *    ONE caller that decides when the entry loader goes (JourneyPage) keeps
 *    it up until the mark has been drawn for {@link LOADER_MIN_VISIBLE_MS},
 *    counted from the moment it WAS drawn (`onShown`) — and ONLY if it was.
 *    A read under 300ms is still released at once, and one that ends after
 *    the mark has had its 500ms is released at once as before; the most it
 *    ever adds is 500ms. AND NEVER OVER AN ERROR
 *    (release review, 2026-09-25 — REQUIREMENTS A8, "a loader never delays an
 *    error"): the caller passes `release` when the wait ended in one (the
 *    clinic read failed, a restore failed), and the loader goes at once — the
 *    error screen is never held behind "Opening your health check".
 *  - …AND ONE SET LOADING TIME, ON ONE CHECKOUT LANE (CTO, 2026-09-25: "Even
 *    for this loader that comes before selecting my plan … Have a set loading
 *    time period."). CheckoutFlow's "Getting your order ready" uses the same
 *    hook with a longer minimum (`CHECKOUT_PREPLAN_MIN_DWELL_MS`, ~3s from the
 *    draw) and passes this SAME card with a checkout journey
 *    (`checkout/checkoutLoaderContent.ts`: what is left — the plan, free
 *    delivery, the card only held, the clinician review — and the entry
 *    card's three chips), whose reveal finishes inside that minimum — but the
 *    card shows ONLY on the lane where checkout was opened from the Result
 *    screen this document submitted, and the hold happens ONLY when that
 *    restore lands on the plan chooser — and then ALONGSIDE the plan screen's
 *    own reads, which that landing starts and the wait includes (release
 *    review 2026-09-25, `checkout/planScreenRead.ts`). Every other resolution
 *    (an error, a failed plan read, a 3D Secure return, a resumed
 *    delivery/payment step, a paid or held order, the account step) passes
 *    `release` and goes the moment the read lands, as it always did; a loader
 *    that never drew holds nothing.
 *  - REDUCED MOTION: the same states on the same clock — the ring stands
 *    still, the glow does not breathe, and the card shows every item at once,
 *    at its full height, with no growth, fade or rise (`LoaderJourneyCard.tsx`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LoaderJourneyCard, type LoaderJourney } from './LoaderJourneyCard';
import { LoaderRing } from './LoaderRing';
import { usePrefersReducedMotion } from './motion';

/** The journey look draws nothing for this long, so a quick read never flashes it. */
export const LOADER_SHOW_DELAY_MS = 300;

/**
 * Once the journey look's mark HAS been drawn, the least time it stays up: its
 * 420ms fade-in (tokens.css P11) and a short rest. Applied by
 * {@link useLoaderHold}, never by the component itself.
 */
export const LOADER_MIN_VISIBLE_MS = 500;

/** What {@link useLoaderHold} remembers from the previous render. */
interface LoaderWait {
  loading: boolean;
  /** Hold the loader until then (0: no hold). */
  until: number;
}

/**
 * The show delay's other half, for a caller that decides when a journey-look
 * loader goes (JourneyPage's entry loader — see "…AND, ONCE DRAWN" in the
 * header). Returns `[show, markShown]`: pass `markShown` to the loader's
 * `onShown`. `show` is `true` while `loading`, and after it, still `true`
 * until the mark has been drawn for {@link LOADER_MIN_VISIBLE_MS} — but only
 * if the mark WAS drawn, so a read quicker than the show delay is never held.
 * A wait that starts again during the hold simply carries on the same loader.
 *
 * `release` (default false): the wait ENDED IN AN ERROR, so nothing is held —
 * `show` is exactly `loading`, and a hold already running is dropped. The
 * anti-flicker minimum is for a wait that led somewhere; an error screen held
 * behind a loader for half a second is a delayed error (REQUIREMENTS A8).
 * CheckoutFlow uses the same switch the other way round: it passes `release`
 * for EVERY wait except the one lane it holds, so everywhere else its loader
 * goes the moment the read lands, exactly as before it used this hook.
 *
 * `minVisibleMs` (default {@link LOADER_MIN_VISIBLE_MS}): how long the drawn
 * mark stays. The ONE caller that passes more is CheckoutFlow's pre-plan lane
 * (`CHECKOUT_PREPLAN_MIN_DWELL_MS`, CTO 2026-09-25: "have a set loading time
 * period"), and only through its own lane guard — see `checkoutLoaderContent.ts`.
 * The rule that a loader which never drew is never held is the same at any
 * minimum.
 *
 * Anchored to the moment the mark was actually drawn (`onShown`), not to the
 * start of the wait: on a slow device the loader's first commit can land well
 * after the render that began the wait, and a hold counted from that render
 * would still cut the fade short — measured on a mobile-emulated dev build,
 * where it left the mark up for ~300ms (review, 2026-09-25).
 *
 * The END of a wait is worked out DURING render, from the previous render's
 * value (React's "storing information from previous renders" pattern), so the
 * render in which the wait ends already knows whether to hold. An effect would
 * paint the next screen for one frame and then mount the loader again, with
 * its show delay restarting.
 */
export function useLoaderHold(
  loading: boolean,
  release = false,
  minVisibleMs: number = LOADER_MIN_VISIBLE_MS,
): readonly [show: boolean, markShown: () => void] {
  // When the mark now on screen was drawn (null: none is, or not yet).
  const drawnAt = useRef<number | null>(null);
  const [wait, setWait] = useState<LoaderWait>({ loading, until: 0 });
  let current = wait;
  // A wait that ended in an error drops any hold at once — including one already
  // running when the error arrives a render later.
  if (wait.loading !== loading || (release && !loading && wait.until !== 0)) {
    const now = Date.now();
    if (loading) {
      // A loader still in its hold carries on, its mark already drawn;
      // otherwise a fresh one mounts, and nothing of it is drawn yet.
      if (wait.until <= now) drawnAt.current = null;
      current = { loading, until: 0 };
    } else {
      current = {
        loading,
        until: release || drawnAt.current === null ? 0 : drawnAt.current + minVisibleMs,
      };
    }
    setWait(current);
  }
  const { until } = current;
  const holding = !loading && until > Date.now();

  useEffect(() => {
    if (!holding) return;
    // Ends the hold on the clock; a wait that starts again first clears it.
    const timer = setTimeout(() => setWait((w) => (w.until === until ? { ...w, until: 0 } : w)), until - Date.now());
    return () => clearTimeout(timer);
  }, [holding, until]);

  const markShown = useCallback(() => {
    if (drawnAt.current === null) drawnAt.current = Date.now();
  }, []);

  return [loading || holding, markShown] as const;
}

/** The ring round the seal (px) — its drawn circle is 40/48 of this. */
const HALO_SIZE = 148;

/** The line when a caller names nothing more specific. */
export const DEFAULT_LOADER_MESSAGE = 'Getting things ready';

/** The honest second line after a long wait. */
export const STILL_WORKING_LINE = 'Still working — this can take a few seconds.';

/** How long a wait lasts before the still-working line joins the message. */
export const STILL_WORKING_AFTER_MS = 4000;

export function BrandedLoader({
  message = DEFAULT_LOADER_MESSAGE,
  reassure = true,
  look = 'classic',
  card,
  onShown,
}: {
  /** What is happening, in the patient's words — public-safe (see the header). */
  message?: string;
  /** Show the still-working line after a long wait. `false` only where nothing is being waited on. */
  reassure?: boolean;
  /** `journey` — the journey's page-level first-load look (see the header); default renders as it always has. */
  look?: 'classic' | 'journey';
  /**
   * The journey look's content card: the journey, revealed one item after
   * another under the words (see the header and `LoaderJourneyCard.tsx`).
   * Public-safe copy only. Pass a STABLE object (a module constant). Ignored by
   * the classic look.
   */
  card?: LoaderJourney;
  /**
   * The journey look only: told when its mark is first DRAWN (the show delay
   * has passed) — `useLoaderHold`'s `markShown`. Must be stable.
   */
  onShown?: () => void;
} = {}) {
  const reduced = usePrefersReducedMotion();
  const [slow, setSlow] = useState(false);
  const journey = look === 'journey';
  const [shown, setShown] = useState(!journey);

  // Counted from MOUNT, not from the latest message: JourneyPage keeps ONE
  // loader up across several reads and swaps its line as they progress, and
  // the patient has been waiting for all of them. With `reassure` off the
  // clock never starts, so `slow` can never become true — the ONE guard.
  useEffect(() => {
    if (!reassure) return;
    const timer = setTimeout(() => setSlow(true), STILL_WORKING_AFTER_MS);
    return () => clearTimeout(timer);
  }, [reassure]);

  // The journey look's show delay — once, from mount (never re-armed).
  useEffect(() => {
    if (shown) return;
    const timer = setTimeout(() => setShown(true), LOADER_SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shown]);

  // The mark is drawn: tell the caller that decides when this goes.
  useEffect(() => {
    if (journey && shown) onShown?.();
  }, [journey, shown, onShown]);

  if (journey) {
    return (
      // A plain layout box: neither busy nor live (see the header).
      <div className="gps-jux-entry" data-shown={shown ? 'true' : 'false'} data-motion={reduced ? 'reduced' : 'full'}>
        {shown ? (
          // The busy part is the mark and nothing else — a SIBLING of the live
          // region below, never its ancestor. Decorative throughout: the words
          // carry the meaning, and the header already names the brand.
          <div aria-busy="true" className="gps-jux-entry-mark">
            <span aria-hidden="true" className="gps-jux-entry-glow" />
            <span aria-hidden="true" className="gps-jux-entry-seal">
              <span className="gps-jux-entry-logo" />
            </span>
            <span aria-hidden="true" className="gps-jux-entry-halo">
              <LoaderRing size={HALO_SIZE} reduced={reduced} variant="halo" />
            </span>
          </div>
        ) : null}
        {/* In the DOM from mount, EMPTY until the mark shows — so the words
            are a change to the region, and a change is what gets announced. */}
        <div role="status" className="gps-jux-entry-words">
          {shown ? <p className="gps-jux-entry-message">{message}</p> : null}
          {/* The box is reserved, so the late line moves nothing below it. */}
          {shown ? <p className="gps-jux-entry-still">{slow ? STILL_WORKING_LINE : null}</p> : null}
        </div>
        {/* The content card: drawn with the mark (so its first item arrives
            only once the show delay has passed, and the reveal starts there),
            a SIBLING of the status region — never inside it, so nothing it
            reveals is announced. An empty card renders nothing. */}
        {shown && card ? <LoaderJourneyCard journey={card} reduced={reduced} /> : null}
      </div>
    );
  }

  return (
    // A plain layout box: neither busy nor live (see the header).
    <div
      style={{
        display: 'grid',
        justifyItems: 'center',
        gap: 'var(--gps-s-3)',
        padding: 'var(--gps-s-6) var(--gps-s-4)',
        textAlign: 'center',
      }}
    >
      {/* The busy part is the ring's box and nothing else — a SIBLING of the
          live region below, never its ancestor (Firefox + NVDA silence a live
          region under any busy ancestor). */}
      <div aria-busy="true">
        <LoaderRing size={48} reduced={reduced} />
      </div>
      {/* The live region holds the words and nothing else, and no ancestor of it is busy. */}
      <div role="status" style={{ display: 'grid', justifyItems: 'center', gap: 'var(--gps-s-3)' }}>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gps-text)' }}>{message}</p>
        {slow ? (
          // --gps-muted on the journey's flat ivory ground (#56645c on #f6f4ef)
          // is 5.66:1, 5.58:1 at the strongest point of its top wash — AA for
          // this 14px line (re-measured 2026-09-25; the old peach corner it was
          // first measured on was deleted with the gradient). Off the journey
          // the kit muted is 5.8:1 on white.
          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.5, color: 'var(--gps-muted)' }}>{STILL_WORKING_LINE}</p>
        ) : null}
      </div>
    </div>
  );
}
